/**
 * Pagination cursors for persisted chat messages.
 *
 * A session's transcript has one total order: `createdAt`, then `sequence`,
 * then `id`. Timestamps alone are not unique — a whole VM-agent batch can land
 * on one millisecond — so a cursor that carries only a timestamp cannot say
 * where inside a group of tied rows a page ended, and the rest of that group
 * is skipped. A {@link MessagePosition} names the exact row instead.
 */
import * as v from 'valibot';
const EncodedPositionSchema = v.strictTuple([
    v.pipe(v.number(), v.safeInteger()),
    v.pipe(v.number(), v.safeInteger()),
    v.pipe(v.string(), v.nonEmpty()),
]);
/** Orders two positions in the transcript total order (negative when `a` comes first). */
export function compareMessagePositions(a, b) {
    if (a.createdAt !== b.createdAt)
        return a.createdAt < b.createdAt ? -1 : 1;
    if (a.sequence !== b.sequence)
        return a.sequence < b.sequence ? -1 : 1;
    // Message IDs are ASCII (UUIDs/ULIDs), so code-unit order matches SQLite's BINARY collation.
    if (a.id !== b.id)
        return a.id < b.id ? -1 : 1;
    return 0;
}
/** Encodes a position as the `before`/`after` query value `[createdAt,sequence,id]`. */
export function formatMessageCursor(position) {
    return JSON.stringify([position.createdAt, position.sequence, position.id]);
}
/**
 * Decodes a `before`/`after` query value: an integer timestamp or the
 * `[createdAt,sequence,id]` array produced by {@link formatMessageCursor}.
 * Returns null when the value is neither.
 */
export function parseMessageCursor(raw) {
    if (/^-?\d+$/.test(raw)) {
        const timestamp = Number(raw);
        return Number.isSafeInteger(timestamp) ? timestamp : null;
    }
    let decoded;
    try {
        decoded = JSON.parse(raw);
    }
    catch {
        return null;
    }
    const encoded = v.safeParse(EncodedPositionSchema, decoded);
    if (!encoded.success)
        return null;
    const [createdAt, sequence, id] = encoded.output;
    return { createdAt, sequence, id };
}
//# sourceMappingURL=message-cursor.js.map